import { useState } from "react";
import * as Sentry from "@sentry/react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LoaderCircle } from "lucide-react";
import { useLangPrefix } from "../hooks/useLangPrefix";
import LanguageSwitcher from "../components/layout/LanguageSwitcher";
import api from "../lib/api";

export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const { prefix } = useLangPrefix();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/manager/auth/forgot-password", { email: email.trim() });
      setSubmitted(true);
    } catch (err) {
      const status = err?.response?.status
      if (!status || status >= 500) {
        Sentry.captureException(err, {
          tags: { module: 'auth', action: 'forgot_password' },
        })
      }
      const detail = err.response?.data?.detail;
      let msg = t("forgotPassword.genericError");
      if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail)) {
        const parts = detail.map((d) =>
          d && typeof d === "object" && "msg" in d ? d.msg : String(d),
        );
        if (parts.length) msg = parts.join(" ");
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-zinc-100 p-4 text-zinc-900 scheme-light">
      <div className="absolute right-4 top-4 z-10 flex items-center gap-2">
        <LanguageSwitcher />
      </div>
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-indigo-100/70 via-transparent to-cyan-100/50" />

      <div className="w-full max-w-sm relative">
        <div className="text-center mb-8">
          <div className="inline-flex items-center">
            <img
              src="/full_logo.png"
              alt="Growchat"
              className="w-40 h-auto rounded-md object-cover"
            />
          </div>
          <p className="text-sm text-gray-500">{t("login.tagline")}</p>
        </div>

        <div className="card p-6">
          <h2 className="mb-1 text-lg font-semibold text-zinc-900">
            {t("forgotPassword.title")}
          </h2>
          <p className="mb-5 text-sm text-gray-600">
            {t("forgotPassword.subtitle")}
          </p>

          {submitted ? (
            <div className="space-y-4">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm text-emerald-800">
                {t("forgotPassword.successMessage")}
              </div>
              <p className="text-center text-xs text-gray-500">
                <Link
                  to={`${prefix}/login`}
                  className="font-medium text-indigo-600 hover:text-indigo-700"
                >
                  {t("forgotPassword.backToLogin")}
                </Link>
              </p>
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {error}
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-zinc-600">
                    {t("forgotPassword.email")}
                  </label>
                  <input
                    type="email"
                    className="input"
                    placeholder={t("forgotPassword.emailPlaceholder")}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full justify-center py-2.5 mt-1 disabled:opacity-50"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <Spinner /> {t("forgotPassword.submitting")}
                    </span>
                  ) : (
                    t("forgotPassword.submit")
                  )}
                </button>
              </form>

              <p className="text-center text-xs text-gray-500 mt-5">
                <Link
                  to={`${prefix}/login`}
                  className="font-medium text-indigo-600 hover:text-indigo-700"
                >
                  {t("forgotPassword.backToLogin")}
                </Link>
              </p>
              <p className="text-center text-xs text-gray-500 mt-3">
                {t("forgotPassword.noAccount")}{" "}
                <Link
                  to={`${prefix}/signup`}
                  className="font-medium text-indigo-600 hover:text-indigo-700"
                >
                  {t("forgotPassword.signUp")}
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Spinner() {
  return <LoaderCircle className="h-4 w-4 animate-spin" />;
}
